

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LessonFormModal from './AddLessonModal';
import Swal from 'sweetalert2';
import { toast } from "react-toastify";

// --- Constants ---
const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;
const ITEMS_PER_PAGE = 10; 

// --- Helper Components ---
const ActionButton = ({ onClick, children, className }) => (
    <button onClick={onClick} className={`p-1.5 rounded-md transition-colors ${className}`}>
        {children}
    </button>
);

const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" /></svg>;

// --- Main Component ---
const ManageLessonTable = () => {
    const [lessons, setLessons] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [editingLessonId, setEditingLessonId] = useState(null);

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [lessonType, setLessonType] = useState(''); // 'normal', 'bonus', or '' for all
    const [activeFilters, setActiveFilters] = useState({ query: '', type: '' });

    const fetchAllLessons = useCallback(async (page, filters) => {
        setIsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: page,
                limit: ITEMS_PER_PAGE,
            });
            if (filters.type) {
                params.append('lessonType', filters.type);
            }
            if (filters.query) {
                params.append('searchByTitle', filters.query); 
            }

            const response = await axios.get(`${API_BASE_URL}/all-lessons?${params.toString()}`);
            
            if (response?.data?.success) {
                setLessons(response.data.data || []);
                setCurrentPage(response.data.page || 1);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
                if (response.data.data.length === 0 && response.data.totalItems > 0 && page > 1) {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                }
            } else { throw new Error(response?.data?.error || 'Invalid response'); }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed to fetch lessons.';
            setError(msg);
            setLessons([]); setTotalPages(0); setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllLessons(currentPage, activeFilters);
    }, [currentPage, activeFilters, fetchAllLessons]);


    useEffect(() => {
    const delayDebounce = setTimeout(() => {
        setCurrentPage(1);
        setActiveFilters({ query: searchQuery.trim(), type: lessonType });
    }, 500); // wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
}, [searchQuery, lessonType]);

    const handleSearch = () => {
        setCurrentPage(1); 
        setActiveFilters({ query: searchQuery, type: lessonType });
    };

    const handleReset = () => {
        setCurrentPage(1);
        setSearchQuery('');
        setLessonType('');
        setActiveFilters({ query: '', type: '' });
    };
    
    // FIX: New handler to trigger search on dropdown change
    const handleLessonTypeChange = (newType) => {
        setLessonType(newType); // Update the visual state of the dropdown
        setCurrentPage(1); // Reset to page 1 for new filter
        setActiveFilters({ query: searchQuery, type: newType }); // Trigger the API call
    };

    const handleSuccess = (message, mode) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 3000); 
        if (mode === 'add' && (currentPage !== 1 || activeFilters.query || activeFilters.type)) {
             handleReset(); 
        } else {
            fetchAllLessons(currentPage, activeFilters);
        }
    };
    
const handleDelete = async (lessonId, lessonTitle) => {
    const result = await Swal.fire({
        title: `Are you sure you want to delete this lesson?`,
        text: "This will permanently delete the lesson. This action cannot be undone.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete lesson!',
        cancelButtonText: 'Cancel',
        customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
    });

    if (result.isConfirmed) {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.delete(`${API_BASE_URL}/delete-lesson/${lessonId}`, { headers });

            toast.success(response.data.message || 'Lesson deleted successfully.');
            fetchAllLessons(currentPage, activeFilters);
        } catch (err) {
            console.error('Error deleting lesson:', err);
            toast.error(err.response?.data?.error || 'Failed to delete lesson.');
        } 
        
    }
};

    
    const handleOpenModalForAdd = () => {
        setModalMode('add');
        setEditingLessonId(null);
        setIsModalOpen(true);
    };

    const handleOpenModalForEdit = (lessonId) => {
        setModalMode('edit');
        setEditingLessonId(lessonId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);
    
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !isLoading) {
            setCurrentPage(newPage);
        }
    };
    
    const PaginationControls = () => {
        if (!totalPages || totalPages <= 0) return null;
        const getPageNumbers = () => {
            const pageNumbers = [];
            const maxPagesToShow = 5;
            const halfMaxPages = Math.floor(maxPagesToShow / 2);
            if (totalPages <= maxPagesToShow) {
                for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
            } else {
                let startPage = Math.max(currentPage - halfMaxPages, 1);
                let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
                if (endPage - startPage < maxPagesToShow - 1) {
                    startPage = Math.max(endPage - maxPagesToShow + 1, 1);
                }
                if (startPage > 1) {
                    pageNumbers.push(1, '...');
                }
                for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
                if (endPage < totalPages) {
                    pageNumbers.push('...', totalPages);
                }
            }
            return pageNumbers;
        };
        // FIX: Simplified pagination text to match image
        const pageDisplayInfo = `Page ${currentPage} of ${totalPages}`;
        return (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white px-4 py-3 border-t border-gray-200 rounded-b-lg">
                <div className="text-sm text-gray-700 mb-2 sm:mb-0">{pageDisplayInfo}</div>
                {totalPages > 1 && (
                    <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}>Previous</button>
                        {getPageNumbers().map((page, index) => (
                            typeof page === 'number' ? (
                                <button key={index} onClick={() => handlePageChange(page)} disabled={isLoading} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${currentPage === page ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{page}</button>
                            ) : (
                                <span key={index} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">{page}</span>
                            )
                        ))}
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed`}>Next</button>
                    </nav>
                )}
            </div>
        );
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full ">
            <div className="container mx-auto max-w-full">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3 sm:mb-0">Manage Lessons</h1>
                    <button onClick={handleOpenModalForAdd} className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                        Add New Lesson
                    </button>
                </div>
                

                <div className="mb-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                       <div className="relative w-full ">
                            {/* FIX: Label removed to match the image */}
                             <label htmlFor="lesson-type" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <input type="text" id="search-title" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter lesson title..."/>
                          {searchQuery && (
                            <button
                            type="button"
                            className="absolute right-2 top-[66%] transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            onClick={() => {
                                setSearchQuery('');
                                setActiveFilters((prevFilters) => ({ ...prevFilters, query: '' }));
                                setCurrentPage(1); // Reset to page 1 after clearing
                            }}
                            >
                            ✕
                            </button>
                        )}
                        
                        </div>
                        <div>
                            <label htmlFor="lesson-type" className="block text-sm font-medium text-gray-700 mb-1">Lesson Type</label>
                            {/* FIX: Added new onChange handler to trigger search automatically */}
                            <select id="lesson-type" value={lessonType} onChange={(e) => handleLessonTypeChange(e.target.value)} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                <option value="">All Types</option>
                                <option value="normal">Normal Lesson</option>
                                <option value="bonus">Bonus Lesson</option>
                            </select>
                        </div>
                        <div className="flex space-x-2">
                            <button onClick={handleSearch} className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Search</button>
                            <button onClick={handleReset} className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">Reset</button>
                        </div>
                    </div>
                </div>

                <LessonFormModal isOpen={isModalOpen} onClose={handleCloseModal} onSuccess={handleSuccess} mode={modalMode} lessonId={editingLessonId} />

                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            {/* Table head and body remain the same */}
                             <thead className="bg-gray-100">
                                <tr>
                                    <th className="sticky left-0 bg-gray-100 px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider z-20 shadow-sm">Sr No.</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Lesson Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Title</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Type</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Lvl</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Invest</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Effort</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Style</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Tech</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Group</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Prio</th>
                                    <th className="sticky right-0 bg-gray-100 px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider z-20 shadow-sm">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                   <tr><td colSpan="12" className="p-10 text-center text-gray-500">Loading...</td></tr>
                                ) : lessons.length === 0 ? (
                                    <tr><td colSpan="12" className="p-10 text-center text-gray-500">No lessons found. { (activeFilters.query || activeFilters.type) && 'Try adjusting your search filters.' }</td></tr>
                                ) : (
                                    lessons.map((lesson, index) => (
                                        <tr key={lesson._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="sticky left-0 bg-white hover:bg-gray-50 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 z-10">
                                                {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.lessonNumber}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-medium max-w-sm truncate">{lesson.lessonTitle}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{lesson.lessonType || 'normal'}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.level}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">${lesson.minInvestment}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.effortLevel}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.workStyle}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.techComfort}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.group}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{lesson.priority}</td>
                                            <td className="sticky right-0 bg-white hover:bg-gray-50 px-6 py-4 whitespace-nowrap text-sm font-medium z-10">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <ActionButton onClick={() => handleOpenModalForEdit(lesson._id)} className="text-blue-600 hover:bg-blue-100">
                                                        <EditIcon />
                                                    </ActionButton>
                                                    <ActionButton onClick={() => handleDelete(lesson._id, lesson.lessonTitle)} className="text-red-600 hover:bg-red-100">
                                                        <DeleteIcon />
                                                    </ActionButton>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <PaginationControls/>
                </div>
            </div>
        </div>
    );
};

export default ManageLessonTable;